/**
 * Home — the consumer front door.
 *
 * Promise first ("now it can"), then a free diagnosis anyone can run before
 * committing to anything. All copy comes from the locale files; all fix counts
 * come from src/data/catalog.json, which is regenerated from the real .jfx
 * packages on every build. Every interaction is instrumented for GA4.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { doc, setDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  diagnose, readCrashReport, totalFixes, vendorFixes, groupCount, CHIP_GROUPS,
  type Diagnosis, type GroupId, type CrashResult,
} from '../lib/diagnose'
import {
  trackCtaClick, trackDiagnose, trackDiagnosisShown, trackExplainTech,
  trackCrashReport, trackPlanSelect, trackSignupSubmit, trackSignupSuccess, trackSignupError,
} from '../lib/analytics'
import { useScrollDepth, useSectionViews } from '../hooks/useAnalytics'
import { PartnerSupportStrip } from '../components/PartnerSupportStrip'
import { AdSenseSlot } from '../components/AdSenseSlot'

const SECTIONS = ['how', 'safety', 'fixes', 'proof', 'price', 'join'] as const

const EXAMPLE_CRASH = [
  '---- Minecraft Crash Report ----',
  '// Why is it breaking :(',
  '',
  'Time: 2026-08-19 18:42:07',
  'Description: Mod loading error has occurred',
  '',
  'net.minecraftforge.fml.ModLoadingException: Missing or unsupported mandatory dependencies:',
  "    Mod 'createaddition' (createaddition) requires 'geckolib' version [4.0,)",
  '    at net.minecraftforge.fml.ModLoader.gatherAndInitializeMods(ModLoader.java:333)',
  '',
  '-- System Details --',
  'Details:',
  '    Minecraft Version: 1.20.1',
  '    Java Version: 17.0.8, Eclipse Adoptium',
  '    Memory: 3000000000 bytes / 4294967296 bytes up to 4294967296 bytes',
  '    JVM Flags: 3 total; -Xmx4G -Xms4G -XX:+UseG1GC',
  '    Loaded Mods:',
  '        forge: Minecraft Forge 47.2.0',
  '        createaddition: Create Crafts & Additions 1.2.3',
].join('\n')

type Answer = (Diagnosis | CrashResult) & { crashKey?: string }

const Home = () => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [crashOpen, setCrashOpen] = useState(false)
  const [crashText, setCrashText] = useState('')
  const [dragging, setDragging] = useState(false)
  const resultRef = useRef<HTMLDivElement>(null)

  useScrollDepth()
  useSectionViews(SECTIONS)

  const tr = <T,>(key: string) => t(key, { returnObjects: true }) as T

  const show = useCallback((next: Answer, group: GroupId) => {
    setAnswer(next)
    trackDiagnosisShown({ problem_group: group, fix_count: next.fixCount })
    requestAnimationFrame(() =>
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    )
  }, [])

  const run = useCallback((text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    const result = diagnose(trimmed)
    trackDiagnose({
      method: 'typed',
      matched: result.group !== 'unknown',
      problem_group: result.group,
      query_length: trimmed.length,
    })
    show(result, result.group)
  }, [show])

  /** Chips know their own group, so they work in every language. */
  const runChip = useCallback((index: number, label: string) => {
    const group = CHIP_GROUPS[index] ?? 'unknown'
    setQuery(label)
    trackDiagnose({ method: 'chip', matched: group !== 'unknown', problem_group: group })
    show({ group, fixCount: groupCount(group) }, group)
  }, [show])

  const runCrash = useCallback((text: string, method: 'paste' | 'drop' | 'example') => {
    const result = readCrashReport(text)
    trackCrashReport({ method, failure_type: result.failureType })
    trackDiagnose({ method: 'crash_report', matched: result.failureType !== 'not_a_crash_report', problem_group: 'games' })
    show({ ...result, crashKey: result.failureType }, 'games')
  }, [show])

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer?.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result)
      setCrashText(text)
      runCrash(text, 'drop')
    }
    reader.readAsText(file)
  }

  const answerKey = useMemo(() => {
    if (!answer) return null
    const crashKey = (answer as Answer).crashKey
    return crashKey ? `home.answers.crash.${crashKey}` : `home.answers.${answer.group}`
  }, [answer])

  const chips = tr<Array<{ label: string }>>('home.ask.chips')
  const steps = tr<Array<{ title: string; body: string }>>('home.how.steps')
  const fixCards = tr<Array<{ id: GroupId; title: string; body: string }>>('home.fixes.cards')
  const proofCards = tr<Array<{ title: string; lede: string; body: string }>>('home.proof.cards')
  const writers = tr<Array<{ who: string; body: string }>>('home.proof.writers')
  const plans = tr<Array<{ id: 'free' | 'unlimited' | 'per_fix'; name: string; amount: string; period: string; for: string; cta: string; features: string[] }>>('home.pricing.plans')

  return (
    <div className="jfx">
      {/* ============ HERO + THE HOOK ============ */}
      <section className="jfx-hero">
        <div className="jfx-wrap">
          <h1>
            {t('home.hero.title')}
            <span className="jfx-now">{t('home.hero.title_now')}</span>
          </h1>
          <p className="jfx-sub">
            {t('home.hero.sub_1')} <b>{t('home.hero.sub_strong')}</b>{t('home.hero.sub_2')}
          </p>
          <div className="jfx-badges">
            {tr<string[]>('home.hero.badges').map((b) => <span key={b}>{b}</span>)}
          </div>

          <div className="jfx-asker">
            <div className="jfx-ask-body">
              <label className="jfx-ask-label" htmlFor="jfx-ask">{t('home.ask.label')}</label>
              <p className="jfx-ask-hint" id="jfx-ask-hint">{t('home.ask.hint')}</p>
              <div className="jfx-ask-row">
                <input
                  id="jfx-ask"
                  type="text"
                  autoComplete="off"
                  aria-describedby="jfx-ask-hint"
                  placeholder={t('home.ask.placeholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') run(query) }}
                />
                <button className="jfx-btn" type="button" onClick={() => run(query)}>
                  {t('home.ask.submit')}
                </button>
              </div>
              <div className="jfx-chips">
                <span className="jfx-chips-lead">{t('home.ask.chips_lead')}</span>
                {chips.map((c, i) => (
                  <button key={c.label} type="button" onClick={() => runChip(i, c.label)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {answer && answerKey && (
              <div className="jfx-result" ref={resultRef} aria-live="polite">
                <div className="jfx-verdict">
                  <div className={`jfx-badge${answer.group !== 'unknown' ? ' ready' : ''}`}>
                    {answer.group !== 'unknown' ? '✓' : '?'}
                  </div>
                  <div>
                    <h3>{t(`${answerKey}.head`, { subject: (answer as CrashResult).subject ?? '' })}</h3>
                    <p className="jfx-plain">
                      {t(`${answerKey}.plain`, {
                        subject: (answer as CrashResult).subject ?? '',
                        memory: (answer as CrashResult).crash?.memoryGb ?? '',
                        minecraft: (answer as CrashResult).crash?.minecraft ?? '',
                      })}
                    </p>
                  </div>
                </div>

                <div className="jfx-plan">
                  <p className="jfx-plan-title">{t('home.result.plan_title')}</p>
                  <ul>
                    {tr<string[]>(`${answerKey}.plan`).map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </div>

                <details className="jfx-tech" onToggle={(e) => {
                  if ((e.currentTarget as HTMLDetailsElement).open) trackExplainTech(answer.group)
                }}>
                  <summary>{t('home.result.explain')}</summary>
                  <div className="jfx-techbody"><p>{t(`${answerKey}.tech`)}</p></div>
                </details>

                <div className={`jfx-strip${answer.fixCount > 0 ? ' ready' : ''}`}>
                  {answer.fixCount > 0 ? (
                    <><b>{t('home.result.ready', { count: answer.fixCount })}</b> {t('home.result.ready_sub')}</>
                  ) : (
                    <><b>{t('home.result.offer')}</b> {t('home.result.offer_sub')}</>
                  )}
                </div>

                <InlineSignup source={`result_${answer.group}`} />

                <div className="mt-5">
                  <PartnerSupportStrip tone="warm" />
                </div>

                {/* Google ad, separate from the affiliate strip and labeled on its own. Renders nothing until a unit id exists. */}
                <AdSenseSlot className="mt-5" />

                {answer.group === 'games' && (
                  <div className="jfx-cta-row">
                    <button
                      className="jfx-link-btn"
                      type="button"
                      onClick={() => setCrashOpen((v) => !v)}
                    >
                      {t('home.result.crash_toggle')}
                    </button>
                  </div>
                )}

                {crashOpen && (
                  <div
                    className={`jfx-deep${dragging ? ' hot' : ''}`}
                    onDragEnter={(e) => { e.preventDefault(); setDragging(true) }}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                    onDragLeave={(e) => { e.preventDefault(); setDragging(false) }}
                    onDrop={onDrop}
                  >
                    <label className="sr-only" htmlFor="jfx-crash">{t('home.result.crash_toggle')}</label>
                    <textarea
                      id="jfx-crash"
                      spellCheck={false}
                      placeholder={t('home.result.crash_placeholder')}
                      value={crashText}
                      onChange={(e) => setCrashText(e.target.value)}
                    />
                    <div className="jfx-deep-foot">
                      <button className="jfx-link-btn" type="button" onClick={() => {
                        setCrashText(EXAMPLE_CRASH); runCrash(EXAMPLE_CRASH, 'example')
                      }}>{t('home.result.crash_example')}</button>
                      <button className="jfx-btn" type="button" onClick={() => runCrash(crashText, 'paste')}>
                        {t('home.result.crash_submit')}
                      </button>
                    </div>
                    <p className="jfx-where">{t('home.result.crash_where')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how">
        <div className="jfx-wrap">
          <div className="jfx-sec-intro">
            <p className="jfx-eyebrow">{t('home.how.eyebrow')}</p>
            <h2>{t('home.how.heading')}</h2>
          </div>
          <div className="jfx-steps">
            {steps.map((s, i) => (
              <div className="jfx-stepcard" key={s.title}>
                <span className="jfx-cue">{i + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center' }}>
            <Link className="jfx-tech-link" to="/for-geeks"
              onClick={() => trackCtaClick({ location: 'safety', label: 'technical_version' })}>
              {t('home.how.tech_link')}
            </Link>
          </p>
        </div>
      </section>

      {/* ============ WHAT IT FIXES ============ */}
      <section id="fixes" className="jfx-band">
        <div className="jfx-wrap">
          <div className="jfx-sec-intro">
            <p className="jfx-eyebrow">{t('home.fixes.eyebrow')}</p>
            <h2>{t('home.fixes.heading')}</h2>
            <p>{t('home.fixes.sub', { total: totalFixes })}</p>
          </div>
          <div className="jfx-cats">
            {fixCards.map((c) => (
              <div className={`jfx-cat${c.id === 'games' ? ' live' : ''}`} key={c.id}>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
                <span className="jfx-state">{t('home.fixes.count_label', { count: groupCount(c.id) })}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW DO WE KNOW ============ */}
      <section id="proof">
        <div className="jfx-wrap">
          <div className="jfx-sec-intro">
            <p className="jfx-eyebrow">{t('home.proof.eyebrow')}</p>
            <h2>{t('home.proof.heading')}</h2>
            <p>{t('home.proof.sub')}</p>
          </div>

          <div className="jfx-assure">
            <div className="jfx-acard feature">
              <div>
                <h3>{t('home.proof.vendor.title')}</h3>
                <p className="jfx-lede">{t('home.proof.vendor.lede')}</p>
              </div>
              <div className="jfx-feat-body">
                <p>{t('home.proof.vendor.body_1')}</p>
                <p>{t('home.proof.vendor.body_2', { vendor: vendorFixes })}</p>
              </div>
            </div>

            <div className="jfx-acard feature spotlight">
              <div>
                <span className="jfx-spark">{t('home.proof.ai.spark')}</span>
                <h3>{t('home.proof.ai.title')}</h3>
                <p className="jfx-lede">{t('home.proof.ai.lede')}</p>
              </div>
              <div className="jfx-feat-body">
                <p>{t('home.proof.ai.body_1')}</p>
                <p>{t('home.proof.ai.body_2')}</p>
              </div>
            </div>

            {proofCards.map((c) => (
              <div className="jfx-acard" key={c.title}>
                <h3>{c.title}</h3>
                <p className="jfx-lede">{c.lede}</p>
                <p>{c.body}</p>
              </div>
            ))}
          </div>

          <p className="jfx-wsub">{t('home.proof.writers_heading')}</p>
          <div className="jfx-writers">
            {writers.map((w) => (
              <div key={w.who}>
                <p className="jfx-who">{w.who}</p>
                <p>{w.body}</p>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', marginTop: 36 }}>
            <a className="jfx-btn" href="#join"
              onClick={() => trackCtaClick({ location: 'safety', label: 'sign_up_now' })}>
              {t('home.result.cta')}
            </a>
          </p>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="price" className="jfx-band">
        <div className="jfx-wrap">
          <div className="jfx-sec-intro">
            <p className="jfx-eyebrow">{t('home.pricing.eyebrow')}</p>
            <h2>{t('home.pricing.heading')}</h2>
            <p>{t('home.pricing.sub')}</p>
          </div>
          <div className="jfx-pricing">
            {plans.map((plan) => (
              <div className={`jfx-pcard${plan.id === 'unlimited' ? ' featured' : ''}`} key={plan.id}>
                {plan.id === 'unlimited' && <span className="jfx-pflag">{t('home.pricing.flag')}</span>}
                <p className="jfx-pname">{plan.name}</p>
                <p className="jfx-pamount">{plan.amount}<span>{plan.period}</span></p>
                <p className="jfx-pfor">{plan.for}</p>
                <ul className="jfx-pfeat">
                  {plan.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <a
                  className={`jfx-btn jfx-pbtn${plan.id === 'unlimited' ? '' : ' quiet'}`}
                  href="#join"
                  onClick={() => {
                    trackPlanSelect(plan.id)
                    trackCtaClick({ location: 'pricing', label: plan.cta, plan: plan.id })
                  }}
                >{plan.cta}</a>
                {plan.id === 'unlimited' && (
                  <p className="jfx-pfamily">
                    <b>{t('home.pricing.family')}</b><br />{t('home.pricing.family_sub')}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="jfx-pnote">{t('home.pricing.note')}</p>
        </div>
      </section>

      {/* ============ JOIN ============ */}
      <section id="join" className="jfx-signup">
        <div className="jfx-narrow">
          <h2>{t('home.join.heading')}</h2>
          <p>{t('home.join.sub')}</p>
          <InlineSignup source="final_cta" big />
        </div>
      </section>
    </div>
  )
}

/** Email capture. Writes to the same Firestore collection the old form used. */
const InlineSignup = ({ source, big = false }: { source: string; big?: boolean }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const normalized = email.toLowerCase().trim()
    if (!normalized.includes('@')) {
      setMessage('Enter an email address so we know where to send it.')
      setStatus('error')
      trackSignupError(source, 'invalid_email')
      return
    }
    setStatus('sending')
    trackSignupSubmit(source)
    try {
      if (!db) throw new Error('firestore_unavailable')
      await setDoc(doc(db, 'interest_list', normalized), {
        email: normalized,
        timestamp: new Date(),
        source,
      })
      setStatus('done')
      setEmail('')
      trackSignupSuccess(source)
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: 'waitlist_signup' }, extra: { source } })
      const reason = error instanceof Error ? error.message : 'unknown'
      setMessage("That didn't go through. Try again in a moment.")
      setStatus('error')
      trackSignupError(source, reason)
    }
  }

  if (status === 'done') {
    return <p className="jfx-signup-done">You're in. We'll email you the moment it's ready.</p>
  }

  return (
    <form className={`jfx-joinform${big ? ' big' : ''}`} onSubmit={submit}>
      <label className="sr-only" htmlFor={`jfx-email-${source}`}>{t('home.result.email_label')}</label>
      <input
        id={`jfx-email-${source}`}
        type="email"
        autoComplete="email"
        placeholder={t('home.result.email_placeholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <button className="jfx-btn" type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : big ? t('home.join.cta') : t('home.result.email_cta')}
      </button>
      {status === 'error' && <p className="jfx-form-error" role="alert">{message}</p>}
    </form>
  )
}

export default Home
